import { gql, useQuery } from '@apollo/client';
import CashflowByYearStackedChartThumbnail from '@assets/images/report-components/cashflow-by-year-stacked-chart-thumbnail.png';
import { useMemo, useState } from 'react';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import Graph from '@/components/Graphs/graph';
import { useChartColors } from '@/hooks/useChartColors';
import type { AssetAggregatesStacked } from '@/types/GraphqlSchema';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';
import { getLayout } from './helpers';

const QUERY = gql`
  query assetAggregatesStacked($input: AssetAggregatesInput!) {
    assetAggregatesStacked(input: $input) {
      data {
        type
        group
        freeCashflowSum
        values
      }
      stacks
    }
  }
`;

export const COMPONENT_ID = 'cashflow_by_year_stacked';

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<AssetAggregatesStacked>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { loading, error } = useQuery<{ assetAggregatesStacked: AssetAggregatesStacked }>(QUERY, {
    variables: {
      input: {
        by: 'cashflowYear',
        fields: ['freeCashflowSum'],
        stackBy: 'country',
        portfolio,
      },
    },
    skip: !!persistedData,
    onCompleted: response => {
      if (response?.assetAggregatesStacked) {
        const chartData = response.assetAggregatesStacked;
        setChartDataFetched(chartData);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: chartData,
          });
        }
      }
    },
  });

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<AssetAggregatesStacked>) => {
  const chartColors = useChartColors();
  const formattedData = useMemo(() => {
    try {
      const { data: chartData, stacks } = data;

      // Create bar traces for each stack (country)
      const barTraces = stacks.map((stackName, idx) => {
        const x = chartData.map(item => item.group); // Time periods (e.g., "Jan 2024")
        const y = chartData.map(item => {
          const stackValues = item.values[stackName];
          return stackValues?.freeCashflowSum ?? 0;
        });

        return {
          x,
          y,
          type: 'bar',
          name: stackName,
          stackgroup: stackName,
          marker: {
            color: chartColors[idx % chartColors.length],
          },
        };
      });

      // Calculate total cashflow sum for each time period using freeCashflowSum
      const totalCashflowSum = chartData.map(item => item.freeCashflowSum || 0);

      // Create line trace for total cashflow
      const lineTrace = {
        x: chartData.map(item => item.group),
        y: totalCashflowSum,
        type: 'scatter',
        mode: 'lines',
        name: 'Total Cashflow',
        line: {
          color: '#000000',
          width: 3,
        },
        marker: {
          size: 8,
          color: '#000000',
        },
        yaxis: 'y',
      };

      // Combine bar traces and line trace
      return [...barTraces, lineTrace];
    } catch {
      return null;
    }
  }, [data, chartColors]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Cashflow By Year (Stacked by Country)</ComponentTitle>
      <Graph
        data={formattedData}
        layout={getLayout({ yaxis: { title: { text: 'Cashflow (US$ million)' } } })}
        title={undefined}
        config={undefined}
        xlabel={undefined}
        ylabel={undefined}
        loading={undefined}
        onUpdate={undefined}
        onClick={undefined}
        onRelayout={undefined}
        onInitialized={undefined}
        onAfterPlot={undefined}
        maxY={undefined}
        darkTheme={undefined}
        context={undefined}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregatesStacked>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Cashflow By Year (Stacked by Country)
    </ComponentTitle>

    <img
      src={CashflowByYearStackedChartThumbnail}
      alt='Cashflow By Year Stacked Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const CashflowByYearStackedChartComponent: ComponentDefinition<AssetAggregatesStacked> = {
  id: COMPONENT_ID,
  name: 'Cashflow By Year',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Corporate],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
